import * as React from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import isNaN from 'lodash/isNaN';
import isNull from 'lodash/isNull';
import { useTranslation } from 'react-i18next';

import ProductCard, {
  CardImage,
  CardSubSubTitle,
} from '../../components/ProductCard/ProductCard';

import { showMinMaxPrice } from '../utils';
import Button from '../../components/Button';
import { Paragraph } from '../../components/Typography';
import { H3 } from '../../components/Typography';
import Breakpoints from '../../design-utils/breakpoints';
import { addonIsBaB } from './useAddons';

const CURRENCY = get(window, ['Shopify', 'currency', 'active'], 'USD');
const DEFAULT_SECTION = 'default';

interface AddonsModalContentProps {
  addons: GlobalProduct[] | GlobalVariant[];
  selectAddon: (id: number, isBundle: boolean) => void;
}
const AddonsModalContentSections = (
  props: AddonsModalContentProps,
): JSX.Element | null => {
  const { t } = useTranslation();

  const sections = chunkProductsInSections(props.addons as GlobalProduct[]);
  useTitlesOnScreen(Object.keys(sections));
  const { [DEFAULT_SECTION]: others, ...productsSections } = sections;

  const sectionsMenu = Object.keys({
    ...productsSections,
    ...(others?.length && { [DEFAULT_SECTION]: [] }),
  }).map((section) => (
    <SectionTitleButton
      id={`button-${section}`}
      key={section}
      onClick={() => scrollToSection(section)}
    >
      {section === DEFAULT_SECTION ? t('bundles.additional_products') : section}
    </SectionTitleButton>
  ));

  const objectsInSections = Object.entries(productsSections)?.map(
    ([section, products]) => (
      <MainModalContent id={`section-${section}`} key={`title-${section}`}>
        <div data-testid="addons-modal">
          <SectionTitle
            key={`title-${section}`}
            id={section}
            modifiers={['display']}
          >
            {section}
          </SectionTitle>
          <main className="addons-modal-content">
            {products.map((addon) => (
              <ProductCard key={addon.id} id={`${addon.id}`}>
                <CardImage image={addon.image} alt={addon.title} />
                <CardSubSubTitle
                  className="bundle-product-card__subtitle"
                  modifiers={['body']}
                >
                  {addon.title}
                </CardSubSubTitle>
                <Button
                  fullWidth
                  filled
                  onClick={() =>
                    props.selectAddon(
                      parseInt(`${addon.id}`),
                      addonIsBaB(addon),
                    )
                  }
                  disabled={!addon.available}
                >
                  {addon.available
                    ? addonIsBaB(addon)
                      ? t('product_swap.customize_bundle')
                      : t('addons.select')
                    : t('product_swap.sold_out')}
                </Button>
                <Paragraph
                  modifiers={{
                    body: true,
                    subdued: !!addon.discountedPrice,
                  }}
                  style={{
                    marginTop: 20,
                    textDecoration: addon.discountedPrice
                      ? 'line-through'
                      : 'none',
                  }}
                >
                  {parseInt(addon.priceMax as string) === 0 &&
                  parseInt(addon.priceMin as string) === 0
                    ? null
                    : showMinMaxPrice({
                        currency: addon.priceCurrency || CURRENCY,
                        max: addon.priceMax as number,
                        min: addon.priceMin as number,
                      })}
                </Paragraph>

                {!isNull(addon.discountedPrice) ||
                !isNaN(addon.discountedPrice) ? (
                  <DiscountedPrice>
                    {showMinMaxPrice({
                      currency: addon.priceCurrency || CURRENCY,
                      max: addon.discountedPriceMax as number,
                      min: addon.discountedPriceMin as number,
                    })}
                  </DiscountedPrice>
                ) : null}
              </ProductCard>
            ))}
          </main>
        </div>
      </MainModalContent>
    ),
  );

  if (others?.length) {
    objectsInSections.push(
      <MainModalContent
        id={`section-${DEFAULT_SECTION}`}
        key={`title-${DEFAULT_SECTION}`}
      >
        <div data-testid="addons-modal">
          <SectionTitle
            key={`title-${DEFAULT_SECTION}`}
            id={DEFAULT_SECTION}
            modifiers={['display']}
          >
            {t('bundles.additional_products')}
          </SectionTitle>
          <main className="addons-modal-content">
            {others.map((addon) => (
              <ProductCard key={addon.id} id={`${addon.id}`}>
                <CardImage image={addon.image} alt={addon.title} />
                <CardSubSubTitle
                  className="bundle-product-card__subtitle"
                  modifiers={['body']}
                >
                  {addon.title}
                </CardSubSubTitle>
                <Button
                  fullWidth
                  filled
                  onClick={() =>
                    props.selectAddon(
                      parseInt(`${addon.id}`),
                      addonIsBaB(addon),
                    )
                  }
                  disabled={!addon.available}
                >
                  {addon.available
                    ? addonIsBaB(addon)
                      ? t('product_swap.customize_bundle')
                      : t('addons.select')
                    : t('product_swap.sold_out')}
                </Button>
                <Paragraph
                  modifiers={{
                    body: true,
                    subdued: !!addon.discountedPrice,
                  }}
                  style={{
                    marginTop: 20,
                    textDecoration: addon.discountedPrice
                      ? 'line-through'
                      : 'none',
                  }}
                >
                  {parseInt(addon.priceMax as string) === 0 &&
                  parseInt(addon.priceMin as string) === 0
                    ? null
                    : showMinMaxPrice({
                        currency: addon.priceCurrency || CURRENCY,
                        max: addon.priceMax as number,
                        min: addon.priceMin as number,
                      })}
                </Paragraph>

                {!isNull(addon.discountedPrice) ||
                !isNaN(addon.discountedPrice) ? (
                  <DiscountedPrice>
                    {showMinMaxPrice({
                      currency: addon.priceCurrency || CURRENCY,
                      max: addon.discountedPriceMax as number,
                      min: addon.discountedPriceMin as number,
                    })}
                  </DiscountedPrice>
                ) : null}
              </ProductCard>
            ))}
          </main>
        </div>
      </MainModalContent>,
    );
  }
  return (
    <>
      <NavSections>{sectionsMenu}</NavSections>
      {objectsInSections}
    </>
  );
};

const MainModalContent = styled.main.attrs<{ id: string }>(({ id }) => ({
  'data-testid': 'addons-modal-sections',
  className: 'addons-modal-content-sections',
  id: id,
}))`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const DiscountedPrice = styled.span.attrs({
  className: 'discounted-addons-price',
})`
  font-weight: 600;
  font-size: 16px;
`;

const chunkProductsInSections = (
  products: GlobalProduct[],
): Record<string, GlobalVariant[] | GlobalProduct[]> => {
  const sections = {};
  products.forEach((productData: GlobalProduct) => {
    const section = productData.productType;
    if (section === '') {
      if (sections[DEFAULT_SECTION]) {
        sections[DEFAULT_SECTION].push(productData);
      } else {
        sections[DEFAULT_SECTION] = [productData];
      }
    }
    if (section) {
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(productData);
    }
  });
  return sections;
};

const useTitlesOnScreen = (titles: string[] = []) => {
  React.useEffect(() => {
    titles.forEach((title) => {
      const titleElement = document.getElementById(`section-${title}`);
      const observer = getObserver(title);

      if (titleElement) {
        observer.observe(titleElement);
      }
      return () => {
        observer.unobserve(titleElement as Element);
      };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // We only want to run this effect once

  return true;
};

const getObserver = (title: string, rootMargin = '0px') => {
  return typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(
        ([entry]) => {
          const buttonTitle = document.getElementById(`button-${title}`);
          if (entry.isIntersecting) buttonTitle?.classList.add('showed');
          else buttonTitle?.classList.remove('showed');
        },
        {
          rootMargin,
          threshold: 0.2,
        },
      )
    : {
        observe: () => void {},
        unobserve: () => void {},
      };
};

const scrollToSection = (section: string) => {
  const sectionElement = document.getElementById(section);
  const scrollableElement = document.querySelector(
    '.addons-scrollable-section',
  );
  if (sectionElement && scrollableElement) {
    // Top height of the element
    const elementHeightPosition = sectionElement.offsetTop;
    const stickyHeaderHeight = calculateStickyHeaderHeight();
    scrollableElement.scrollTo({
      top: elementHeightPosition - stickyHeaderHeight,
      behavior: 'smooth',
    });
  }
};

export default AddonsModalContentSections;

const SectionTitleButton = styled(Button)`
  text-transform: uppercase;
  font-weight: 400;
  &:hover {
    font-weight: 700;
  }

  &.showed {
    font-weight: 700;
  }
`;

const SectionTitle = styled(H3).attrs({
  className: 'bundle-section__title',
})`
  text-align: center;
  text-transform: uppercase;
  margin: 1rem 0;
`;

const NavSections = styled.nav.attrs({
  className: 'bundle-section__nav',
})`
  position: sticky;
  inset: 0;
  top: -20px;
  width: 100%;
  margin: 0 auto;
  padding: 1rem 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  z-index: 1;

  @media (max-width: ${Breakpoints.sm}) {
    display: none;
  }
`;

const calculateStickyHeaderHeight = () => {
  const headerSection = document.querySelector('.header-section');
  const navHeader = document.querySelector('.bundle-section__nav');
  const stickyHeader = document.querySelector(
    '.bundleapp-modal__container--complete-screen--header-container',
  );
  const stepperHeader = document.querySelector('.bundle-stepper-header');
  const productOptionsHeader = document.querySelector(
    '.awtomatic-bab-options-container',
  );
  const progressBar = document.querySelector('.awtomatic-progress-container');

  const headerSectionHeight = headerSection?.clientHeight || 0;
  const navHeaderHeight = navHeader?.clientHeight || 0;
  const stickyHeaderHeight = stickyHeader?.clientHeight || 0;
  const stepperHeaderHeight = stepperHeader?.clientHeight || 0;
  const productOptionsHeaderHeight = productOptionsHeader?.clientHeight || 0;
  const progressBarHeight = progressBar
    ? window.getComputedStyle(progressBar).order === '1'
      ? progressBar.clientHeight
      : 0
    : 0;

  return (
    progressBarHeight +
    headerSectionHeight +
    productOptionsHeaderHeight +
    stepperHeaderHeight +
    stickyHeaderHeight +
    navHeaderHeight +
    40
  ); // Add some extra space
};
